import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import SearchMap from 'components/search/v2/SearchMap/SearchMap';
import {
  getMaxWidthQuery,
  getMinQueryWidth,
  getMinWidthQuery,
  mediaMLgMin,
  mediaMMdMin,
  mediaMSmMax,
} from 'helpers/breakpoints';
import BoatCard from 'components/search/v2/BoatCard/BoatCard';
import Filter from 'components/search/v2/Filter/Filter';
import BoatsListHeader, { composeHeadline } from 'components/search/v2/BoatsListHeader';
import EmptyResultsMessageWithReset from 'components/search/v2/EmptyResultsMessageWithReset';
import PaginationComponent from 'components/Pagination';
import { FlexAlignCenterSpaceBetween, FlexCenterBoth } from 'components/primitives/flex';
import {
  useLoadFilterStateFromQuery,
  useSyncPrevBoatsResponse,
  useSyncPushFilterStateToQuery,
} from 'components/search/v2/Filter/hooks';
import {
  areBoatsLoadingAtom,
  currentPageAtom,
  DSUQueryNameAtom,
  filterApiPayloadAtom,
  filteredBoatsAtom,
  filteredBoatsMetaAtom,
  geoTargetsAtom,
  isMapVisibleAtom,
  isRouterReadyAtom,
  nearParamAtom,
  perPageAtom,
  totalBoatsCountAtom,
  zoomLevelAtom,
} from 'components/search/v2/Filter/filterStore';
import { useBreakpoint } from 'components/BreakpointProvider';
import Script from 'next/script';
import getConfig from 'next/config';
import MobileMapToggleButton from 'components/search/v2/MobileMapToggleButton';
import {
  bannerHeight,
  breakpoints,
  filterBarHeight,
  filterBarHeightMobile,
  headerHeight,
} from 'components/search/v2/styleConstants';
import ToggleMapSwitch from 'components/search/v2/ToggleMapSwitch';
import { otherFiltersDropdownVisibleAtom } from 'components/search/v2/Filter/contents/OtherFiltersFilterButton';
import { useMediaQuery } from 'beautiful-react-hooks';
import { getItunesAppUniversalLinkMetaTag, RENTER_APP_ID } from 'helpers/templateHelpers';
import Head from 'next/head';
import useResetPageOnFilterPayloadChange from 'components/search/v2/hooks/useResetPageOnFilterPayloadChange';
import { RedirectBanner } from 'components/search/v2/RedirectBanner';
import useAbandonEffect from 'utils/tracking/useAbandonEffect';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { getAsString } from 'helpers';
import boatsetterPaths from 'utils/boatsetterPaths';
import { createRudderStackPageEvent } from '../../_app';
import { constructQueryStringWithoutEncode } from '../../../src/app/stacks/search/queryHelpers';
import { locationIsDomesticSearch, tokenizeSearchLocationString } from '../../../src/app/stacks/search/helpers';
import { getBoatCardUrl } from '../../../src/app/components/search/v2/utils';
import { useBoatPositionPriceTrack } from '../../../src/app/components/search/v2/hooks/useBoatPositionPriceTrack';

const { GOOGLE_API } = getConfig().publicRuntimeConfig;

const SearchPageV2 = () => {
  const router = useRouter();

  const totalBoatsCount = useAtomValue(totalBoatsCountAtom);
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [isMapVisible, toggleIsMapVisible] = useAtom(isMapVisibleAtom);
  const otherFiltersDropdownVisible = useAtomValue(otherFiltersDropdownVisibleAtom);

  const filteredBoats = useAtomValue<[]>(filteredBoatsAtom);
  const filteredBoatsMeta = useAtomValue(filteredBoatsMetaAtom);
  const areBoatsLoading = useAtomValue(areBoatsLoadingAtom);
  const near = useAtomValue(nearParamAtom);
  const routerReady = useAtomValue(isRouterReadyAtom);
  const geoTargets = useAtomValue(geoTargetsAtom);
  const activeBreakpoints = useBreakpoint();
  const smallScreen = activeBreakpoints.xs || activeBreakpoints.sm;
  const mapCanShowBreakpoint = useMediaQuery(getMinQueryWidth(breakpoints.MAP_CAN_SHOW));

  const perPage = useAtomValue(perPageAtom);
  const filterApiPayload = useAtomValue(filterApiPayloadAtom);
  const setZoomLevel = useSetAtom(zoomLevelAtom);
  const DSUQueryName = useAtomValue(DSUQueryNameAtom);
  const headline = composeHeadline(near || DSUQueryName);

  useSyncPrevBoatsResponse();
  useSyncPushFilterStateToQuery(router);
  useLoadFilterStateFromQuery(router);
  useResetPageOnFilterPayloadChange();

  useEffect(() => {
    // Toggle the map when breakpoint switches
    toggleIsMapVisible(mapCanShowBreakpoint);
  }, [mapCanShowBreakpoint]);

  // url guard
  useEffect(() => {
    if (!router.isReady) return;

    // redirect to HP, if either we have 0 params or (near params is missing, and it is not DSU page)
    if (
      !Object.keys(router.query).length ||
      (!router.query.near && !(!!router.query.loc_physical_ms || !!router.query.loc_interest_ms))
    ) {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }

    // redirect to HP if this condition of DSU passes
    if (!router.query.loc_interest_ms && !!router.query.loc_physical_ms && router.query.ctype === 'om') {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }
    // checks for global url
    const queryNear = router.query.near as string;
    if (queryNear) {
      const tokens = tokenizeSearchLocationString(queryNear || '');
      const isDomestic = locationIsDomesticSearch(tokens, queryNear);

      const zoomLevel = router.query.zoom_level as string;
      setZoomLevel(parseInt(zoomLevel, 10));

      if (!isDomestic && router.query.ne_lat && router.query.ne_lng && router.query.sw_lat && router.query.sw_lng) {
        // for some reason global has ne - sw swapped ..... funny
        const globalPayload = {
          near: router.query.near,
          latitudeSw: router.query.sw_lat,
          longitudeSw: router.query.sw_lng,
          latitudeNe: router.query.ne_lat,
          longitudeNe: router.query.ne_lng,
          start_date: router.query.start_date,
          end_date: router.query.end_date,
          availableFrom: undefined,
        };

        if (router.query.trip_date) {
          globalPayload.availableFrom = router.query.trip_date;
        }
        window.location.replace(
          `https://www.boatsetter.com/global/search?${constructQueryStringWithoutEncode(globalPayload)}`
        );
      }
    }
  }, [router.query]);

  useEffect(() => {
    // redirect to HP if we cannot find DSU page
    if (geoTargets.state === 'hasError') {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }
  }, [geoTargets]);

  const changePage = useCallback(
    (nextPage) => {
      setCurrentPage(nextPage);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    const root = document.getElementById('__next');
    root.scrollTo(0, 0);
  }, [filteredBoats]);

  useEffect(() => {
    const boats = filteredBoats.map((boat: any, index) => ({ position: index, boatId: boat.boat_public_id }));
    if (boats.length > 0) createRudderStackPageEvent(router, 1, { boats });
  }, [filteredBoats, router]);

  useBoatPositionPriceTrack(filteredBoats, areBoatsLoading, routerReady);

  useAbandonEffect({
    boat_id: null,
    abandon_type: 'plp',
    trip_start: null,
    trip_finish: null,
    conversation_url: null,
    last_searched_plp_url: true,
    last_searched_pdp_url: null,
    abandoned_checkout_url: null,
    last_searched_location: near,
    last_searched_trip_time: router.query.trip_date ? `${router.query.trip_date} 00:00:00` : null,
  });

  const showRedirectBanner = getAsString(router.query.redirected_from_pdp) === 'true';

  return (
    <>
      <Head>{getItunesAppUniversalLinkMetaTag(router.asPath, RENTER_APP_ID)}</Head>
      <Head>
        <meta name="robots" content="noindex, follow" />
      </Head>
      <DefaultLayout noFollow={false} isSearch searchBar hideFooter={smallScreen}>
        <Script
          strategy="beforeInteractive"
          src={`https://maps.googleapis.com/maps/api/js?libraries=places,geometry&key=${GOOGLE_API}`}
          key="google-maps"
        />
        <ContentLayout showRedirectBanner={showRedirectBanner} mapActive={isMapVisible}>
          {showRedirectBanner && <RedirectBanner />}
          <FilterBar showRedirectBanner={showRedirectBanner} otherFiltersVisible={otherFiltersDropdownVisible}>
            <Filter data-testid="search-filter-box" />
            <ToggleSwitchVisibleWrapper>
              <ToggleMapSwitch isMapVisible={isMapVisible} toggleMap={toggleIsMapVisible} />
            </ToggleSwitchVisibleWrapper>
          </FilterBar>
          <BoatsContainer isMapVisible={isMapVisible}>
            {/* {featureFlags?.top_location_chips && <TopLocationsHeader />} */}
            <BoatsListHeader />

            {filteredBoats.length > 0 ? (
              <BoatsList isMapVisible={isMapVisible}>
                {filteredBoats.map((boat: any, index: number) => (
                  <BoatCard
                    key={boat.boat_public_id}
                    url={getBoatCardUrl({
                      filterApiPayload,
                      boatId: boat.boat_public_id,
                      pos: (currentPage - 1) * perPage + index,
                    })}
                    boat={boat as any}
                    index={index}
                    searchLocation={headline}
                    currentPage={currentPage}
                  />
                ))}
              </BoatsList>
            ) : (
              <EmptyResultsMessageWithReset />
            )}

            {filteredBoats.length > 0 && (
              <StyledPaginationComponent total={totalBoatsCount} handlePageClick={changePage} page={currentPage} />
            )}
          </BoatsContainer>

          <MapWrapper isMapVisible={isMapVisible} data-testid="search-map">
            <SearchMap searchMetaData={filteredBoatsMeta} searchResults={filteredBoats} />
          </MapWrapper>
          {areBoatsLoading && <Loading />}
        </ContentLayout>
        <MobileMapToggleButton />
      </DefaultLayout>
    </>
  );
};

interface ContentLayoutProps {
  mapActive: boolean;
  showRedirectBanner: boolean;
}

const ContentLayout = styled.div<ContentLayoutProps>`
  padding-top: 60px;
  display: grid;
  grid-template-rows: 80px auto;
  grid-template-areas:
    'filter filter'
    'boats boats';

  ${mediaMSmMax} {
    grid-template-rows: 70px auto;
  }

  ${({ showRedirectBanner }) =>
    showRedirectBanner &&
    css`
      grid-template-rows: ${bannerHeight} 80px auto;
      grid-template-areas:
        'redirect-banner redirect-banner'
        'filter filter'
        'boats boats';
      ${mediaMSmMax} {
        grid-template-rows: ${bannerHeight} 80px auto;
      }
    `}

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    grid-template: 80px auto / auto 32vw;
    grid-template-areas:
      'filter filter'
      '${({ mapActive }) => (mapActive ? 'boats map' : 'boats boats')}';

    ${({ showRedirectBanner }) =>
      showRedirectBanner &&
      css<ContentLayoutProps>`
        grid-template: ${bannerHeight} 80px auto / auto 32vw;
        grid-template-areas:
          'redirect-banner redirect-banner'
          'filter filter'
          '${({ mapActive }) => (mapActive ? 'boats map' : 'boats boats')}';

        ${mediaMSmMax} {
          grid-template-rows: ${bannerHeight} 80px auto;
        }
      `}
  }
`;

const FilterBar = styled(FlexAlignCenterSpaceBetween)<{ otherFiltersVisible: boolean; showRedirectBanner: boolean }>`
  grid-area: filter;
  background-color: #fff;
  padding: 0 22px;
  position: sticky;
  top: ${({ showRedirectBanner }) => (showRedirectBanner ? `calc(${headerHeight} + ${bannerHeight})` : headerHeight)};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  z-index: ${({ otherFiltersVisible }) => (otherFiltersVisible ? 2000 : 20)};

  ${mediaMSmMax} {
    height: ${filterBarHeightMobile};
    padding: 15px 0px;
    position: fixed;
    width: 100%;
  }

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    border-bottom: 1px solid #dbdfe5;
    padding: 0 42px;
  }
`;

const BoatsContainer = styled.div<{ isMapVisible: boolean }>`
  grid-area: boats;
  padding: 12px 22px 22px;
  min-height: calc(100vh - 70px - 60px);
  position: relative;

  ${mediaMMdMin} {
    padding: 28px ${({ isMapVisible }) => (isMapVisible ? '24px' : '42px')} 28px 42px;
  }
`;

const BoatsList = styled.div<{ isMapVisible: boolean }>`
  display: grid;
  gap: 24px;

  ${getMinWidthQuery(breakpoints.BOATS_TWO_COLS)} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${getMinWidthQuery(breakpoints.BOATS_THREE_COLS)} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${({ isMapVisible }) =>
    !isMapVisible &&
    css`
      ${mediaMLgMin} {
        grid-template-columns: repeat(3, 1fr) !important;
      }

      ${getMinWidthQuery(breakpoints.BOATS_FOUR_COLS)} {
        grid-template-columns: repeat(4, 1fr) !important;
      }
    `}
`;

const MapWrapper = styled.div<{ isMapVisible }>`
  grid-area: map;
  max-height: calc(100vh - ${headerHeight} - ${filterBarHeight});
  position: sticky;
  top: calc(${headerHeight} + ${filterBarHeight});
  display: ${({ isMapVisible }) => (isMapVisible ? 'block' : 'none')};

  ${getMaxWidthQuery(breakpoints.MAP_CAN_SHOW - 1)} {
    position: fixed;
    inset: 0;
    z-index: 10;
    top: calc(${headerHeight} + ${filterBarHeight});
    max-height: unset;
  }

  ${mediaMSmMax} {
    top: calc(${headerHeight} + ${filterBarHeightMobile});
  }
`;

const Loading = styled((props) => (
  <div {...props}>
    <FlexCenterBoth height="100vh">
      <LoadingSpinner color="#72D4BA" />
    </FlexCenterBoth>
  </div>
))`
  background-color: #0000006c;
  backdrop-filter: blur(2px);
  position: fixed;
  inset: 0;
`;

const ToggleSwitchVisibleWrapper = styled.div`
  display: none;

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    display: block;
  }
`;

const StyledPaginationComponent = styled(PaginationComponent)`
  margin-top: 16px;
`;

SearchPageV2.getInitialProps = async ({ req, res, query }) => {
  if (!req && !res) return {};

  // redirect to HP if this condition of DSU passes
  if (!query.loc_interest_ms && !!query.loc_physical_ms && query.ctype === 'om') {
    res.writeHead(302, { Location: `/${req.url.replace(boatsetterPaths.search, '')}` });
    res.end();
    return;
  }

  return {
    props: {},
  };
};

export default SearchPageV2;
